import { Fragment, useEffect } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import useCollections from 'networking/mindfeed/hooks/useCollections';
import { useMemoFormContext } from 'elements/MemoForm/context/FormContext';
import { useTranslations } from 'next-intl';

export const CollectionSelectorForm: React.FC = () => {
  const t = useTranslations();
  const { data: collections, isLoading } = useCollections();
  const { watch, setValue } = useMemoFormContext();

  // Watch the current `collectionId` value from the form state
  const selectedCollectionId = watch('collectionId', null);

  // Handle change in selected collection
  const handleChange = (id: string | null) => {
    setValue('collectionId', id); // Update the form state
  };

  // Automatically reset `collectionId` if the selected collection no longer exists
  useEffect(() => {
    if (
      selectedCollectionId &&
      !collections?.find((col) => col.id === selectedCollectionId)
    ) {
      setValue('collectionId', null); // Reset to null if collection is invalid
    }
  }, [selectedCollectionId, collections, setValue]);

  const selectedCollection =
    collections?.find((col) => col.id === selectedCollectionId) || null;

  return isLoading ? (
    <Fragment />
  ) : (
    <Listbox
      value={selectedCollection ? selectedCollection.id : null}
      onChange={handleChange}
    >
      <ListboxButton
        className={classNames(
          styles.listbox.btn.container.default,
          selectedCollection
            ? styles.listbox.btn.container.active
            : styles.listbox.btn.container.inactive,
        )}
      >
        {selectedCollection ? (
          <span className={styles.listbox.btn.emoji}>
            {selectedCollection.emoji || '#'}
          </span>
        ) : (
          <FontAwesomeIcon
            icon={faPlus}
            className={styles.listbox.btn.plusIcon}
          />
        )}
        <span className={styles.listbox.btn.txt}>
          {selectedCollection?.name ?? t('memoCard.collectionPlaceholder')}
        </span>
      </ListboxButton>

      <ListboxOptions
        anchor={{ to: 'bottom start', gap: '4px' }}
        className={styles.listbox.options.container}
      >
        <ListboxOption
          key="All"
          value={null}
          className={({ selected }) =>
            classNames(
              styles.listbox.options.element.container.default,
              selected
                ? styles.listbox.options.element.container.active
                : styles.listbox.options.element.container.inactive,
            )
          }
        >
          <div className={styles.listbox.options.element.left.container}>
            <span className={styles.listbox.options.element.left.emoji}>#</span>
          </div>
          <div className={styles.listbox.options.element.txt}>
            {t('memoCard.collectionPicker.dontAssign')}
          </div>
        </ListboxOption>
        {collections &&
          collections.map((collection) => (
            <ListboxOption
              key={collection.id}
              value={collection.id}
              className={({ selected }) =>
                classNames(
                  styles.listbox.options.element.container.default,
                  selected
                    ? styles.listbox.options.element.container.active
                    : styles.listbox.options.element.container.inactive,
                )
              }
            >
              <div className={styles.listbox.options.element.left.container}>
                <span className={styles.listbox.options.element.left.emoji}>
                  {collection.emoji ? collection.emoji : '#'}
                </span>
              </div>
              <div className={styles.listbox.options.element.txt}>
                {collection.name}
              </div>
            </ListboxOption>
          ))}
      </ListboxOptions>
    </Listbox>
  );
};

const styles = {
  listbox: {
    btn: {
      container: {
        default: classNames(
          'flex flex-row items-center gap-1 shrink-0 rounded-full border py-1 px-2.5 text-left text-xs max-w-40',
          'focus:outline-hidden data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-white/25',
        ),
        active:
          'bg-accent-tint text-accent-tint-ink font-medium border-accent-tint-ink/30',
        inactive:
          'bg-surface-card text-ink-secondary border-stroke hover:bg-surface-field',
      },
      emoji: '',
      plusIcon: 'text-[9px]',
      txt: 'truncate',
    },
    options: {
      container: classNames(
        // z-[60] keeps the portaled dropdown above the modal panel (z-50);
        // without it the menu renders behind the opaque sheet and is invisible.
        'z-[60] w-(--button-width) rounded-xl shadow-sm bg-surface-card p-1 [--anchor-gap:var(--spacing-1)] [--anchor-max-height:14rem] focus:outline-hidden ring-1 ring-hairline',
        'min-w-52',
      ),
      element: {
        container: {
          default:
            'group flex cursor-default items-center gap-2 rounded-lg py-1.5 px-2 select-none data-focus:bg-ink-secondary/10 data-selected:bg-accent-tint cursor-pointer',
          active: 'bg-accent-fill',
          inactive: '',
        },
        left: {
          container:
            'rounded-lg bg-ink-secondary/20 w-8 h-8 flex shrink-0 items-center justify-center group-data-selected:bg-accent-fill',
          emoji: 'group-data-selected:text-white',
        },
        icon: 'invisible size-4 fill-white group-data-selected:visible',
        txt: 'text-sm/6 text-ink-primary truncate',
      },
    },
  },
};
